import {
  Button,
  Box,
  Typography,
  CircularProgress,
  Tooltip,
  AlertColor,
  Snackbar,
  Alert,
} from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { useState, useEffect } from "react";
import EditOperatorModal from "./EditOperatorModal";
import NewOperatorModal from "./NewOperatorModal";
import { getJwtTokenFromIdToken } from "../auth/AuthFanc";
import UpdateButton from "../../atoms/Button/UpdateButton";
import {
  CustomContainer,
  StyledDataGrid,
  customDataGridLocaleTextAll,
  customDataGridLocaleTextLoading,
  customDataGridLocaleTextNoData,
} from "../../../styles/styles";
import IconButtonWithTooltip from "../../atoms/Button/IconButtonWithTooltip";
import EditIcon from "@mui/icons-material/Edit";
import React from "react";
import ConfirmDialog from "../../molecules/ConfirmDialog";

export type OperatorType = "autolock" | "social";

export const operatorTypeToDisplayText = (x: OperatorType) => {
  return x === "autolock" ? "FTスマートキー" : "ミライロドア";
};

export interface Operator {
  autoOperatorId: number;
  operatorId: string;
  password: string;
  operatorName: string;
  departmentName: string;
  operatorPhoneNumber: string;
  operatorEmail: string;
  memo: string;
  operatorType: OperatorType;
  createdAt: string;
}

export const emptyOperator: Operator = {
  autoOperatorId: 0,
  password: "",
  operatorId: "",
  operatorName: "",
  departmentName: "",
  operatorPhoneNumber: "",
  operatorEmail: "",
  createdAt: "",
  memo: "",
  operatorType: "autolock",
};

const OperatorListPage = () => {
  const [data, setData] = useState<Operator[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>(undefined);

  // アラート
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState<AlertColor>("success");
  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
    // window.location.reload();//下部アラートが消えた後に画面リロード
  };

  // 確認ダイアログ
  const [operatorUpdatedConfirmOpen, setOperatorUpdatedConfirmOpen] =
    React.useState(false);
  const [operatorCreatedConfirmOpen, setOperatorCreatedConfirmOpen] =
    React.useState(false);

  useEffect(() => {
    const fetchData = async () => {
      // JWTトークンを取得します
      const jwtToken = await getJwtTokenFromIdToken();

      try {
        // call indexOperators API
        const response = await fetch(
          process.env.REACT_APP_API_ENDPOINT + "/operators",
          {
            headers: {
              Authorization: jwtToken,
            },
          }
        );
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const jsonResponse = await response.json();
        console.log("オペレータ一覧" + JSON.stringify(jsonResponse));
        setData(jsonResponse.operators);
      } catch (error: any) {
        setError(error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const [openNewOperatorModal, setOpenNewOperatorModal] = useState(false);
  const [newOperator, setNewOperator] = useState<Operator>(emptyOperator);
  const handleNewOperatorClick = () => {
    setOpenNewOperatorModal(true);
    console.log(`Operator New Registration button clicked`);
  };

  const handleNewOperatorChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setNewOperator({
      ...newOperator,
      [event.target.name]: event.target.value,
    });
  };

  // handleNewOperator関数の修正（async追加）
  const handleNewOperator = async () => {
    try {
      // JWTトークンを取得します
      const jwtToken = await getJwtTokenFromIdToken();

      // call storeOperator API
      const response = await fetch(
        process.env.REACT_APP_API_ENDPOINT + "/operators",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: jwtToken,
          },
          body: JSON.stringify({
            operatorName: newOperator.operatorName,
            departmentName: newOperator.departmentName,
            operatorPhoneNumber: newOperator.operatorPhoneNumber,
            operatorEmail: newOperator.operatorEmail,
            memo: newOperator.memo,
            operatorType: newOperator.operatorType,
          }),
        }
      );

      const data = await response.json();
      console.log("New Operator Creation Success:", data);
      setOperatorCreatedConfirmOpen(true);
    } catch (error: any) {
      console.error("New Operator Creation Error:", error);
      setSnackbarMessage("オペレーターの作成に失敗しました。");
      setAlertSeverity("error");
      setSnackbarOpen(true);
    }

    // モーダルを閉じて、新規オペレーターデータをリセットする
    setNewOperator(emptyOperator);
    setOpenNewOperatorModal(false);
  };

  const [editOperator, setEditOperator] = useState<Operator>(emptyOperator);
  const [openModal, setOpenModal] = useState(false);
  const handleEditOperatorClick = (autoOperatorId: any) => {
    const operatorToEdit = data.find(
      (operator) => operator.autoOperatorId === autoOperatorId
    );
    if (operatorToEdit) {
      setEditOperator(operatorToEdit);
      setOpenModal(true);
    }
    console.log(
      `Operator Edit button clicked. Selected operator: ${autoOperatorId}`
    );
  };

  const handleEditOperatorChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (editOperator.autoOperatorId > 0) {
      setEditOperator({
        ...editOperator,
        [event.target.name]: event.target.value,
      });
    }
  };

  const handleEditOperatorSubmit = async () => {
    if (editOperator.autoOperatorId > 0) {
      // JWTトークンを取得します
      const jwtToken = await getJwtTokenFromIdToken();

      try {
        // call updateOperator API
        const response = await fetch(
          process.env.REACT_APP_API_ENDPOINT +
            `/operators/${editOperator.autoOperatorId}`,
          {
            method: "PATCH",
            headers: {
              "Content-Type": "application/json",
              Authorization: jwtToken,
            },
            body: JSON.stringify({
              operatorName: editOperator.operatorName,
              departmentName: editOperator.departmentName,
              operatorPhoneNumber: editOperator.operatorPhoneNumber,
              operatorEmail: editOperator.operatorEmail,
              memo: editOperator.memo,
            }),
          }
        );

        const data = await response.json();
        console.log("Edit Operator Success:", data);
        setOperatorUpdatedConfirmOpen(true);
      } catch (error: any) {
        console.error("Edit Operator Error:", error);
        setSnackbarMessage("オペレーターの更新に失敗しました。");
        setAlertSeverity("error");
        setSnackbarOpen(true);
        throw error; // エラーを再スローして、呼び出し元でcatchできるようにする
      }

      // モーダルを閉じて、編集オペレーターデータをリセットする
      setEditOperator(emptyOperator);
      setOpenModal(false);
    }
  };

  if (isLoading) {
    <Box display="flex" justifyContent="center" alignItems="center">
      <CircularProgress /> {/* MUIの CircularProgress コンポーネント */}
    </Box>;
  }

  if (error) {
    return <div>Error: {error.message}</div>;
  }

  const columns: GridColDef[] = [
    {
      field: "operatorType",
      headerName: "サービス種別",
      flex: 1,
      renderCell: (params) => {
        const displayText = operatorTypeToDisplayText(params.value);
        return (
          <Tooltip title={displayText}>
            <span>{displayText}</span>
          </Tooltip>
        );
      },
    },
    {
      field: "operatorId",
      headerName: "ID",
      flex: 1,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "createdAt",
      headerName: "登録日",
      flex: 1,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "operatorName",
      headerName: "氏名(部署名)",
      flex: 1,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "departmentName",
      headerName: "部署名(会社名)",
      flex: 1,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "operatorPhoneNumber",
      headerName: "連絡先(TEL)",
      flex: 1,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "operatorEmail",
      headerName: "連絡先(mail)",
      flex: 1,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "memo",
      headerName: "メモ",
      flex: 2,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "actions",
      headerName: "操作",
      flex: 1,
      renderCell: (params: any) => (
        <IconButtonWithTooltip
          title="編集"
          onClick={() => handleEditOperatorClick(params.row.autoOperatorId)}
          icon={<EditIcon />}
        />
      ),
    },
  ];

  return (
    <CustomContainer maxWidth={false}>
      <Box display="flex" flexDirection="column" marginBottom={2}>
        <Typography variant="h5" component="h2" gutterBottom>
          オペレーター一覧
        </Typography>
        <Box display="flex" justifyContent="flex-end">
          <Button
            variant="outlined"
            color="primary"
            onClick={handleNewOperatorClick}
          >
            新規登録
          </Button>
          <Box marginLeft={1}>
            <UpdateButton />
          </Box>
        </Box>
      </Box>
      <div style={{ height: 700, width: "100%" }}>
        <StyledDataGrid
          rows={data}
          columns={columns}
          getRowId={(row) => row.autoOperatorId}
          disableRowSelectionOnClick
          localeText={
            isLoading
              ? customDataGridLocaleTextLoading
              : data.length === 0
              ? customDataGridLocaleTextNoData
              : customDataGridLocaleTextAll
          }
        />
      </div>

      <NewOperatorModal
        open={openNewOperatorModal}
        handleClose={() => setOpenNewOperatorModal(false)}
        handleNewOperator={handleNewOperator}
        handleNewOperatorChange={handleNewOperatorChange}
        setSnackbarOpen={setSnackbarOpen}
        setAlertSeverity={setAlertSeverity}
        setSnackbarMessage={setSnackbarMessage}
      />

      {editOperator.autoOperatorId > 0 && (
        <EditOperatorModal
          open={openModal}
          handleClose={() => setOpenModal(false)}
          handleEditOperatorSubmit={handleEditOperatorSubmit}
          handleEditOperatorChange={handleEditOperatorChange}
          editOperator={editOperator}
          setEditOperator={setEditOperator}
          setSnackbarOpen={setSnackbarOpen}
          setAlertSeverity={setAlertSeverity}
          setSnackbarMessage={setSnackbarMessage}
        />
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={alertSeverity}
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <ConfirmDialog
        open={operatorCreatedConfirmOpen}
        onClose={() => {
          setOperatorCreatedConfirmOpen(false);
        }}
        onConfirm={() => {
          window.location.reload();
        }}
        title="確認"
        description={
          <>
            オペレーターの作成が完了しました。
            <br />
            IDとパスワードは「オペレータの編集」から確認できます。
          </>
        }
        showCancelButton={false}
      />

      <ConfirmDialog
        open={operatorUpdatedConfirmOpen}
        onClose={() => {
          setOperatorUpdatedConfirmOpen(false);
        }}
        onConfirm={() => {
          window.location.reload();
        }}
        title="確認"
        description="オペレーターの更新が完了しました。"
        showCancelButton={false}
      />
    </CustomContainer>
  );
};

export default OperatorListPage;
